/**
 * MembersTab — member list with roles + lightweight moderator controls.
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, MoreVertical, UserMinus, ShieldPlus, ShieldMinus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { fadeUp, stagger } from '@/lib/motion';
import { useCommunities } from '../context/CommunitiesContext';
import { ROLE_LABELS } from '../constants';
import { CURRENT_USER_ID } from '../constants/seedData';
import EmptyState from '../EmptyState';

const avatarTone = { coral: 'bg-coral', yellow: 'bg-yellow', sage: 'bg-sage', blue: 'bg-blue' };

export default function MembersTab({ community, role }) {
  const { getMembers, loadCommunityMembers, setMemberRole, removeMember } = useCommunities();
  const members = getMembers(community.id);
  const canModerate = role === 'admin';

  useEffect(() => {
    loadCommunityMembers(community.id).catch((error) => toast(error.message || 'We could not load community members.'));
  }, [community.id, loadCommunityMembers]);

  if (members.length === 0) return <div className="u-container max-w-2xl py-8"><EmptyState type="members" /></div>;

  return (
    <div className="u-container max-w-2xl py-8">
      <h2 className="font-display text-xl font-semibold text-ink">Members <span className="text-subtle">({members.length})</span></h2>

      <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 divide-y divide-line rounded-2xl border border-line bg-card shadow-soft">
        {members.map((m, i) => (
          <motion.div key={m.id} variants={fadeUp} custom={i % 10} className="flex items-center gap-3 p-4">
            <span className={cx('grid h-10 w-10 shrink-0 place-items-center rounded-full font-display text-sm font-bold text-[#FFF7F0]', avatarTone[m.tone] ?? avatarTone.coral)}>
              {m.initial}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{m.name}{m.id === CURRENT_USER_ID && <span className="ml-1.5 text-xs font-normal text-subtle">(you)</span>}</p>
              <p className="text-xs text-subtle">{m.booksRead} books read on ScribbleReads</p>
            </div>
            {m.role !== 'member' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-coral/10 px-2 py-0.5 text-[10px] font-semibold text-coral">
                <ShieldCheck className="h-3 w-3" /> {ROLE_LABELS[m.role]}
              </span>
            )}
            {canModerate && m.id !== CURRENT_USER_ID && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="rounded-full p-1.5 text-subtle hover:bg-cream hover:text-ink" aria-label={`Manage ${m.name}`}>
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 rounded-xl border-line bg-card p-1.5 text-ink shadow-xl">
                  {m.role === 'member' ? (
                    <DropdownMenuItem onSelect={() => { setMemberRole(community.id, m.id, 'moderator'); toast(`${m.name} is now a moderator`); }} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                      <ShieldPlus className="h-3.5 w-3.5 text-subtle" /> Make Moderator
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => { setMemberRole(community.id, m.id, 'member'); toast(`${m.name} is now a member`); }} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                      <ShieldMinus className="h-3.5 w-3.5 text-subtle" /> Remove Moderator
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-line" />
                  <DropdownMenuItem onSelect={() => { removeMember(community.id, m.id); toast(`Removed ${m.name} from the community`); }} className="cursor-pointer gap-2.5 rounded-lg text-sm font-medium text-coral focus:bg-coral/10 focus:text-coral">
                    <UserMinus className="h-3.5 w-3.5" /> Remove from Community
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
